var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
// Get icon size based on the bdsSize prop
const getIconSize = (bdsSize, theme) => {
    switch (bdsSize) {
        case 'micro':
            return { width: theme.palette.icon.size.micro, height: theme.palette.icon.size.micro };
        case 'xxsmall':
            return { width: theme.palette.icon.size.xxsmall, height: theme.palette.icon.size.xxsmall };
        case 'xsmall':
            return { width: theme.palette.icon.size.xsmall, height: theme.palette.icon.size.xsmall };
        case 'small':
            return { width: theme.palette.icon.size.small, height: theme.palette.icon.size.small };
        case 'medium':
            return { width: theme.palette.icon.size.medium, height: theme.palette.icon.size.medium };
        case 'large':
            return { width: theme.palette.icon.size.large, height: theme.palette.icon.size.large };
        case 'xlarge':
            return { width: theme.palette.icon.size.xlarge, height: theme.palette.icon.size.xlarge };
        case 'xxlarge':
            return { width: theme.palette.icon.size.xxlarge, height: theme.palette.icon.size.xxlarge };
        default:
            return { width: theme.palette.icon.size.medium, height: theme.palette.icon.size.medium };
    }
};
// Get icon type based on the bdsType prop and other states
const getIconType = (bdsType, theme, disabled, selected, isHovered, isFocused) => {
    switch (bdsType) {
        case 'errorNeutral':
            return { fill: theme.palette.icon.static.neutral.color.error };
        case 'errorSubtle':
            return { fill: theme.palette.icon.static.subtle.color.error };
        case 'errorEmphasis':
            return { fill: theme.palette.icon.static.emphasis.color.error };
        case 'warningNeutral':
            return { fill: theme.palette.icon.static.neutral.color.warning };
        case 'warningSubtle':
            return { fill: theme.palette.icon.static.subtle.color.warning };
        case 'warningEmphasis':
            return { fill: theme.palette.icon.static.emphasis.color.warning };
        case 'infoNeutral':
            return { fill: theme.palette.icon.static.neutral.color.info };
        case 'infoSubtle':
            return { fill: theme.palette.icon.static.subtle.color.info };
        case 'infoEmphasis':
            return { fill: theme.palette.icon.static.emphasis.color.info };
        case 'successNeutral':
            return { fill: theme.palette.icon.static.neutral.color.success };
        case 'successSubtle':
            return { fill: theme.palette.icon.static.subtle.color.success };
        case 'successEmphasis':
            return { fill: theme.palette.icon.static.emphasis.color.success };
        case 'staticNeutral':
            return { fill: theme.palette.icon.static.neutral.color.default };
        case 'staticEmphasis':
            return { fill: theme.palette.icon.static.emphasis.color.default };
        case 'staticSubtle':
            return { fill: theme.palette.icon.static.subtle.color.default };
        case 'interactiveNeutral':
            return Object.assign(Object.assign(Object.assign({ fill: selected
                    ? theme.palette.icon.interactive.neutral.color.selected.default
                    : theme.palette.icon.interactive.neutral.color.default }, (isHovered && {
                fill: selected
                    ? theme.palette.icon.interactive.neutral.color.selected.hover
                    : theme.palette.icon.interactive.neutral.color.hover
            })), (isFocused && { fill: theme.palette.icon.interactive.neutral.color.focus })), (disabled && { cursor: 'default', fill: theme.palette.icon.interactive.neutral.color.disabled }));
        case 'interactiveEmphasis':
            return Object.assign(Object.assign(Object.assign({ fill: selected
                    ? theme.palette.icon.interactive.emphasis.color.selected.default
                    : theme.palette.icon.interactive.emphasis.color }, (isHovered && {
                fill: selected
                    ? theme.palette.icon.interactive.emphasis.color.selected.hover
                    : theme.palette.icon.interactive.emphasis.color.hover
            })), (isFocused && { fill: theme.palette.icon.interactive.emphasis.color.focus })), (disabled && { cursor: 'default', fill: theme.palette.icon.interactive.emphasis.color.disabled }));
        case 'interactiveSubtle':
            return Object.assign(Object.assign(Object.assign({ fill: selected
                    ? theme.palette.icon.interactive.subtle.color.selected.default
                    : theme.palette.icon.interactive.subtle.color.default }, (isHovered && {
                fill: selected
                    ? theme.palette.icon.interactive.subtle.color.selected.hover
                    : theme.palette.icon.interactive.subtle.color.hover
            })), (isFocused && { fill: theme.palette.icon.interactive.subtle.color.focus })), (disabled && { cursor: 'default', fill: theme.palette.icon.interactive.subtle.color.disabled }));
        default:
            return {};
    }
};
// The CustomIcon component with forwardRef applied
const CustomIcon = React.forwardRef((_a, ref) => {
    var { bdsSize = 'medium', bdsType, bdsIcon: IconComponent, selected = false, disabled = false, onClick } = _a, rest = __rest(_a, ["bdsSize", "bdsType", "bdsIcon", "selected", "disabled", "onClick"]);
    const theme = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const handleHover = (event) => {
        setIsHovered(event.type === 'mouseenter');
    };
    const handleFocus = (event) => {
        setIsFocused(event.type === 'focus');
    };
    const iconProps = Object.assign(Object.assign(Object.assign({}, getIconSize(bdsSize, theme)), getIconType(bdsType, theme, disabled, selected, isHovered, isFocused)), rest);
    return typeof IconComponent === 'string' ? (React.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", fill: "none", style: iconProps, onBlur: handleFocus, onClick: onClick, onFocus: handleFocus, onMouseEnter: handleHover, onMouseLeave: handleHover, ref: ref },
        React.createElement("path", { d: IconComponent }))) : (React.createElement(IconComponent, { style: iconProps, sx: Object.assign({}, iconProps), onBlur: handleFocus, onClick: onClick, onFocus: handleFocus, onMouseEnter: handleHover, onMouseLeave: handleHover, ref: ref }));
});
CustomIcon.displayName = 'Icon';
export default CustomIcon;
//# sourceMappingURL=Icon.js.map